// ============================================================
// VCE — SmartMedia: presentación del contenido visual
// ============================================================
// Render por tipo (image/video) + capacidades (gallery, thumbnails,
// counter, poster, placeholder, controls). Consume fitPolicy.
// El motor NO conoce negocios: recibe items + capacidades + config
// del tenant. Toda la PRESENTACIÓN (colores, fondos, assets, hover)
// se resuelve desde la config — el consumidor NO pasa clases visuales.
// (P1/P2/P4/P6)
// Default (sin config) = comportamiento del feed certificado F1
// (paridad absoluta). (P1/P2/P4/P6)
// ============================================================
import React, { useMemo, useState } from 'react';
import type { MediaCapabilities, TenantVisualConfig, VisualItem } from './types';
import { normalizeVisualItems } from './adapters/contentAdapter';

interface RawMediaItem {
  type?: 'image' | 'video';
  url: string;
  poster?: string;
}

// Mapa estático ratio → clase Tailwind (JIT necesita literales)
// '1/1' → aspect-square: clase exacta del viewer histórico (paridad F2)
export const RATIO_CLASS: Record<string, string> = {
  '3/4': 'aspect-[3/4]',
  '1/1': 'aspect-square',
  '16/9': 'aspect-[16/9]',
};

/** Resuelve la clase de ratio: prop explícita > config del tenant > default 3/4 */
export function resolveRatioClass(config?: TenantVisualConfig, explicit?: string): string {
  if (explicit) return explicit;
  const configured = config?.mediaRatios?.default;
  if (configured && RATIO_CLASS[configured]) return RATIO_CLASS[configured];
  return RATIO_CLASS['3/4'];
}

export interface SmartMediaProps {
  /** Contenido crudo del consumidor (media[] tipado o images[] legacy) */
  media?: RawMediaItem[];
  images?: string[];
  blocked?: boolean;
  ratioClass?: string;
  alt?: string;
  className?: string;
  /** Capacidades activas (las decide el motor vía composeDecision + config del tenant) */
  capabilities?: MediaCapabilities;
  /** Configuración del tenant: presentación + textos (P6 — marca del tenant aquí) */
  config?: TenantVisualConfig;
  /** Índice activo controlado (galería) — o interno si no se pasa */
  activeIndex?: number;
  onSelect?: (index: number) => void;
  /** Poster global para videos (contenido del consumidor: images[0]) */
  videoPoster?: string;
}

export const SmartMedia: React.FC<SmartMediaProps> = ({
  media,
  images,
  blocked = false,
  ratioClass,
  alt = '',
  className = '',
  capabilities = {},
  config,
  activeIndex: controlledIndex,
  onSelect,
  videoPoster,
}) => {
  const items = useMemo<VisualItem[]>(() => normalizeVisualItems(media, images), [media, images]);
  const [internalIndex, setInternalIndex] = useState(0);
  const activeIndex = controlledIndex ?? internalIndex;

  // ── Capacidades: config del tenant + prop explícito (el prop gana) ──
  const caps: MediaCapabilities = { ...(config?.capabilities ?? {}), ...capabilities };

  // ── Presentación resuelta desde la config del tenant (nunca del consumidor) ──
  const pres = config?.presentation ?? {};
  const mediaBgClass = pres.mediaBgClass ?? 'bg-gray-100';
  const interactive = pres.interactive ?? true;
  const thumbActiveClass = pres.thumbActiveClass ?? 'border-gray-800 ring-2 ring-gray-300';
  const thumbIdleClass = pres.thumbIdleClass ?? 'border-gray-200 hover:border-gray-400';
  const videoThumbFallback = pres.videoThumbFallback ?? '';
  const counterText = config?.texts?.counter ?? '{n} archivo(s) • {m} video(s)';
  const containerExtra = pres.mediaContainerClass ?? '';
  const resolvedRatioClass = resolveRatioClass(config, ratioClass);

  // Sin media → placeholder (capacidad) o contenedor vacío (legacy feed)
  if (items.length === 0) {
    if (caps.placeholder) {
      return (
        <div className={`${resolvedRatioClass} bg-gray-100 rounded-lg flex items-center justify-center`}>
          <span className="text-gray-400 text-4xl">📷</span>
        </div>
      );
    }
    return <div className={`${resolvedRatioClass} ${mediaBgClass} overflow-hidden relative product-media ${containerExtra}`} />;
  }

  const safeIndex = Math.min(activeIndex, items.length - 1);
  const active = items[safeIndex];
  const isVideo = active.type === 'video';
  const showGallery = !!caps.gallery && items.length > 1;
  const videoCount = items.filter((i) => i.type === 'video').length;
  const withControls = !!caps.controls;

  const mediaClass = interactive && !blocked
    ? 'w-full h-full transition-transform duration-300 hover:scale-105'
    : 'w-full h-full';

  const select = (i: number) => {
    setInternalIndex(i);
    onSelect?.(i);
  };

  const renderCounter = () => {
    return counterText.replace('{n}', String(items.length)).replace('{m}', String(videoCount));
  };

  return (
    <>
      <div className={`${resolvedRatioClass} ${mediaBgClass} overflow-hidden relative product-media ${containerExtra}`}>
        {isVideo ? (
          <video
            key={safeIndex}
            src={active.url}
            muted={!withControls}
            playsInline={!withControls}
            preload="metadata"
            controls={withControls}
            poster={caps.poster ? (active.poster || videoPoster) : undefined}
            className={mediaClass}
          >
            Tu navegador no soporta video.
          </video>
        ) : (
          <img src={active.url} alt={alt} className={mediaClass} />
        )}
      </div>

      {showGallery && (
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2" style={{ WebkitOverflowScrolling: 'touch' }}>
          {items.map((item, i) => (
            <div
              key={item.url + i}
              onClick={() => select(i)}
              className={
                'relative w-16 h-16 rounded-lg overflow-hidden cursor-pointer border-2 flex-shrink-0 transition-all product-media ' +
                (i === safeIndex ? thumbActiveClass : thumbIdleClass)
              }
            >
              {item.type === 'video' ? (
                <div className="w-full h-full bg-gray-800 flex items-center justify-center relative">
                  {videoThumbFallback || item.poster || videoPoster ? (
                    <img
                      src={item.poster || videoPoster || videoThumbFallback}
                      alt=""
                      className="w-full h-full opacity-50"
                    />
                  ) : null}
                  <span className="absolute inset-0 flex items-center justify-center text-white text-lg">▶</span>
                </div>
              ) : (
                <img src={item.url} alt="" className="w-full h-full" />
              )}
            </div>
          ))}
        </div>
      )}

      {!!caps.counter && (
        <div className="mt-2 text-xs text-gray-400 text-center">{renderCounter()}</div>
      )}
    </>
  );
};

export default SmartMedia;
